import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { TPODataContext } from 'contexts/TPOContext';
import { getAllPlansParser } from 'parser/tradePromoOptimization/plansParser';
import { parseCalenderData } from 'parser/tradePromoOptimization/eventsParser';
import { getConfig } from 'utils/helperMethods';
const errorMessage: string = 'Something went wrong!';

interface UpdatedOutputType extends outputType {
    deletedCount: number;
}

interface Props extends HookInputProps {
    showChanges: boolean;
}

function useFetchCalenderData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    extras = {},
    showChanges,
}: Props): UpdatedOutputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    const [countExcludingDelete, setCountExcludingDelete] = useState(0);
    const [deletedCount, setDeletedCount] = useState(0);
    const { dimensionConfigData, kpiConfigData, promoLabelMapping } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
                setCount(0);
                setCountExcludingDelete(0);
                setDeletedCount(0);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { calenderData, total_count, total_count_excluding_deleted } = payload;
            let mergedDataRes: any = [];
            if (calenderData?.length === 0) {
                throw 'No Data';
            }
            const {
                result: parsedData,
                calenderColumns,
                nonCalenderColumns,
                hoverData,
                legendObj,
                deletedCountParser,
            } = parseCalenderData(payload, kpiConfigData, promoLabelMapping, data?.legendObj ?? {}, appConfig);
            let tempDeletedCount = 0;
            //if  offset is different from default offset value, it means request coming from infinite scroll
            if (variables?.offset !== 0 && Array.isArray(data?.result)) {
                //merge  old data, new data
                mergedDataRes = [...data?.result, ...parsedData];
                tempDeletedCount = deletedCount + deletedCountParser;
            } else {
                mergedDataRes = parsedData;
                tempDeletedCount = deletedCountParser;
            }
            setData({ result: mergedDataRes, calenderColumns, nonCalenderColumns, hoverData, legendObj });
            setCount(total_count ?? 0);
            setCountExcludingDelete(total_count_excluding_deleted ?? 0);
            setDeletedCount(tempDeletedCount ?? 0);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setCountExcludingDelete(0);
        setDeletedCount(0);
        setLoading(false);
        setError(error);
    }

    const outputData: UpdatedOutputType = {
        loading,
        error,
        data,
        count: showChanges ? count : countExcludingDelete, //show count excluding deleted records on normal mode as we are hiding deleted records
        deletedCount,
    };
    return outputData;
}

export { useFetchCalenderData };
